/**
 * Ryan O'Dowd
 * 2025-12-14
 * © Copyright 2025 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import {
  GOAL_ROUTE,
  apiSavePublicGoal,
  fetchVerses,
  setActiveGoalId,
} from '@utils/store/actions';
import {
  Linking,
  View,
} from 'react-native';
import React, {
  useState,
} from 'react';
import Button from '@components/common/Button';
import Dialog from '@components/common/Dialog';
import Text from '@components/common/Text';
import TranslationSelectionDialog from '@components/TranslationSelectionDialog';
import getStyles from './styles';
import globals from '@utils/globals';
import hooks from '@hooks';
import utilities from '@utils/utilities';

const BibleInAYearDialog = (props: AddToGoalDialogProps) => {
  const navigation = hooks.useNavigation();
  const dispatch = hooks.useTypedDispatch();
  const i18n = hooks.useI18n();
  const styles = hooks.useStyles(getStyles);

  const [_isTranslationMenuOpen, setIsTranslationMenuOpen] = useState(false);
  const [_isFetchingTranslation, setIsFetchingTranslation] = useState(false);

  const preferredTranslation = hooks.usePreferredTranslation();
  const userGoals = hooks.useTypedSelector((state) => state.goals);
  const [_goalAdded, setGoalAdded] = useState(false);
  const isAlreadyAGoal = !!Object.values(userGoals).find((p: any) => p.id === globals.yearPlanGoalId);

  const translationSize = hooks.useTypedSelector((state) => utilities.humanReadableBytes(utilities.serializedSizeOfObject(state.verses[preferredTranslation.id])));

  return (
    <Dialog
      visible={true}
      onDismiss={() => props.onClose()}
    >{/* @TODO: abstract this modal to it's own component and make available in Settings component as well */}
      <Dialog.Title>The Bible in a Year</Dialog.Title>{/* @TODO: i18n */}
      <Dialog.Content>
        <Text style={styles.dialogText}>The year before creating Wrinkly, we completed a Bible-in-a-year reading plan, the <Text style={styles.httpLink} onPress={() => Linking.openURL('https://www.navigators.org/resource/discipleship-journal-bible-reading-plan/')}>Navigators Bible Reading Plan</Text>.  We invite you to join us in 2026 as we go through the Scriptures together again!</Text>
        <Text style={styles.dialogText}>This plan was selected in part because it is approachable.  With 25 days of readings per month, there is built-in time for days off or to catch up on missed readings.</Text>
        <Text style={styles.dialogText}>{"Whether this is your first time reading through the entire Bible, or you have read through many times, we pray that this plan helps you to spend more time in God's Word this year."}</Text>
        <Text style={styles.dialogText}>We would love for you to join us in 2026!</Text>
        {/* @TODO: option to share this plan with friends */}
        {!(isAlreadyAGoal || _goalAdded) &&
          <Button
            style={styles.previewButton}
            mode='contained'
            loading={_isFetchingTranslation}
            theme={styles.colors.accent}
            onPress={() => {
              setIsFetchingTranslation(true);
              const callback = () => {
                setIsFetchingTranslation(false);
                navigation.navigate(GOAL_ROUTE, isAlreadyAGoal ? undefined : {
                  previewGoalId: globals.yearPlanGoalId,
                });
                props.onClose();
              };
              if (!translationSize.endsWith('MB') || (+translationSize.split(' ')[0]) < 7) { // @TODO: can't rely on this once there are partial texts supported...really need to store a flag for this
                dispatch(fetchVerses(preferredTranslation.id, undefined, undefined, undefined, undefined, callback));
              } else {
                callback();
              }
            }}
          >
            {_isFetchingTranslation ? 'Downloading Bible...' : 'Preview reading plan'}{/* @TODO: i18n */}
          </Button>
        }
      </Dialog.Content>
      <Dialog.Actions>
        <Button
          mode='outlined'
          onPress={() => setIsTranslationMenuOpen(true)}
        >
          {preferredTranslation.abbreviation}
        </Button>

        <View style={styles.spacer} />

        <Button
          mode='text'
          onPress={() => props.onClose()}
        >
          {i18n('cancel')}
        </Button>
        <Button
          mode='contained'
          onPress={() => {
            if (isAlreadyAGoal || _goalAdded) {
              dispatch(setActiveGoalId(globals.yearPlanGoalId));
              navigation.navigate(GOAL_ROUTE, isAlreadyAGoal ? undefined : {
                previewGoalId: globals.yearPlanGoalId,
              });
              props.onClose();
            } else {
              setGoalAdded(true);
              dispatch(apiSavePublicGoal(globals.yearPlanGoalId, preferredTranslation.id));
            }
          }}
        >
          {isAlreadyAGoal || _goalAdded ? 'View plan' : 'Add goal'}{/* @TODO: i18n */}
        </Button>
      </Dialog.Actions>
      <TranslationSelectionDialog
        isOpen={_isTranslationMenuOpen}
        onClose={() => setIsTranslationMenuOpen(false)}
        autoClose={true}
        closeButtonText={i18n('close')}
      />
    </Dialog>
  );
};

class AddToGoalDialogProps {
  onClose: () => void;
};

export default BibleInAYearDialog;
